import React, { useState, useMemo, useCallback } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { ScrollArea, ScrollBar } from '@/components/ui/scroll-area';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ChevronDown, ChevronRight, Search, Filter, X, Plus, Trash2, Pencil, Check, Users2, Send, MessageSquare, RefreshCw, Copy, StickyNote, Save } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { tasksAPI } from '@/lib/api';
import ClientPickerDialog from '@/components/ClientPickerDialog';

const CHECKLIST_STAGES = {
  not_started: { short: '-', label: 'Not Started', color: 'bg-gray-50 text-gray-400' },
  docs_pending: { short: 'DP', label: 'Docs Pending', color: 'bg-amber-50 text-amber-700' },
  in_progress: { short: 'IP', label: 'In Progress', color: 'bg-blue-50 text-blue-700' },
  further_docs_required: { short: 'FD', label: 'Further Docs Required', color: 'bg-orange-50 text-orange-700' },
  partial_completed: { short: 'PC', label: 'Partial Completed', color: 'bg-indigo-50 text-indigo-700' },
  ready_for_review: { short: 'RR', label: 'Ready for Review', color: 'bg-purple-50 text-purple-700' },
  done: { short: 'D', label: 'Done', color: 'bg-green-50 text-green-700' },
};

const ChecklistGrid = ({
  task, subtask, clients, staffMembers, periods, getPeriodLabel,
  canManage, isAdmin, onReload
}) => {
  const [expandedClients, setExpandedClients] = useState(new Set());
  const [filterClient, setFilterClient] = useState('');
  const [newItem, setNewItem] = useState('');
  const [editingItem, setEditingItem] = useState(null);
  const [editingItemValue, setEditingItemValue] = useState('');
  const [clientPickerOpen, setClientPickerOpen] = useState(false);
  const [noteInputs, setNoteInputs] = useState({});
  const [submittingNote, setSubmittingNote] = useState({});
  const [activeTab, setActiveTab] = useState('checklist');
  // Per-client folder path local edit state (Audit boards). Keyed by client_id.
  const [pathDrafts, setPathDrafts] = useState({});
  const [savingPath, setSavingPath] = useState({});
  // Per-item note state — keyed by `${client_id}::${item_name}`. Used by the
  // popover that renders next to each checklist row.
  const [openItemNote, setOpenItemNote] = useState(null); // { clientId, item } | null
  const [itemNoteDraft, setItemNoteDraft] = useState('');
  const [savingItemNote, setSavingItemNote] = useState(false);

  const handleApplyClientPicker = async (selectedIds) => {
    try {
      await tasksAPI.updateSubtask(task.id, subtask.id, { client_ids: selectedIds });
      onReload();
      toast.success('Client list updated');
    } catch (err) {
      toast.error('Failed to update client list');
    }
  };

  const checklistItems = subtask.checklist_items || [];
  const subtaskClients = useMemo(() => subtask.clients || [], [subtask.clients]);

  const getClientName = useCallback((clientId) => {
    return clients.find(c => c.id === clientId)?.name || '?';
  }, [clients]);

  const filteredClients = useMemo(() => {
    const list = filterClient.trim()
      ? subtaskClients.filter(ce => getClientName(ce.client_id).toLowerCase().includes(filterClient.toLowerCase()))
      : subtaskClients;
    // Sort alphabetically by client name (case-insensitive). The portal-wide
    // requirement is that every client list is sorted A→Z; subtask.clients is
    // stored in insertion order, so we sort at render time.
    return [...list].sort((a, b) =>
      getClientName(a.client_id).toLowerCase().localeCompare(getClientName(b.client_id).toLowerCase())
    );
  }, [subtaskClients, filterClient, getClientName]);

  const toggleClient = (clientId) => {
    setExpandedClients(prev => {
      const next = new Set(prev);
      if (next.has(clientId)) next.delete(clientId);
      else next.add(clientId);
      return next;
    });
  };

  const handleStatusChange = async (clientId, itemName, period, newStatus) => {
    try {
      await tasksAPI.updateChecklistItemStatus(task.id, subtask.id, clientId, itemName, period, { status: newStatus });
      onReload();
    } catch (err) {
      toast.error('Failed to update status');
    }
  };

  // Save the folder-path field for a client. Triggered on blur or Enter so we
  // don't fire a request on every keystroke.
  const saveFolderPath = async (clientId) => {
    const draft = pathDrafts[clientId];
    if (draft === undefined) return; // not edited
    const ce = subtaskClients.find(c => c.client_id === clientId);
    const current = ce?.folder_path || '';
    if (draft === current) return;
    setSavingPath(prev => ({ ...prev, [clientId]: true }));
    try {
      await tasksAPI.updateClientFolderPath(task.id, subtask.id, clientId, draft);
      setPathDrafts(prev => { const n = { ...prev }; delete n[clientId]; return n; });
      onReload();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save folder path');
    }
    setSavingPath(prev => ({ ...prev, [clientId]: false }));
  };

  // Copy folder path to clipboard (used by the Copy button next to the path field).
  const copyFolderPath = async (clientId) => {
    const ce = subtaskClients.find(c => c.client_id === clientId);
    const path = ce?.folder_path || '';
    if (!path) { toast.error('No folder path saved yet'); return; }
    try {
      await navigator.clipboard.writeText(path);
      toast.success('Folder path copied');
    } catch (err) {
      toast.error('Copy failed — please copy manually');
    }
  };

  // Open the per-item note popover with the current saved value.
  const openItemNotePopover = (clientId, item) => {
    const ce = subtaskClients.find(c => c.client_id === clientId);
    const existing = ce?.item_notes?.[item] || '';
    setItemNoteDraft(existing);
    setOpenItemNote({ clientId, item });
  };

  const saveItemNote = async () => {
    if (!openItemNote) return;
    const { clientId, item } = openItemNote;
    setSavingItemNote(true);
    try {
      await tasksAPI.updateChecklistItemNote(task.id, subtask.id, clientId, item, itemNoteDraft);
      toast.success(itemNoteDraft.trim() ? 'Note saved' : 'Note cleared');
      setOpenItemNote(null);
      onReload();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save note');
    }
    setSavingItemNote(false);
  };

  const handleAddChecklistItem = async () => {
    if (!newItem.trim()) return;
    const updated = [...checklistItems, newItem.trim()];
    try {
      await tasksAPI.updateSubtask(task.id, subtask.id, { checklist_items: updated });
      toast.success(`Added "${newItem.trim()}"`);
      setNewItem('');
      onReload();
    } catch (err) { toast.error('Failed to add item'); }
  };

  const handleRemoveChecklistItem = async (itemName) => {
    if (!window.confirm(`Remove "${itemName}" from checklist? Status data for this item will be lost.`)) return;
    const updated = checklistItems.filter(i => i !== itemName);
    try {
      await tasksAPI.updateSubtask(task.id, subtask.id, { checklist_items: updated });
      toast.success(`Removed "${itemName}"`);
      onReload();
    } catch (err) { toast.error('Failed to remove item'); }
  };

  const handleRenameChecklistItem = async (oldName) => {
    const newName = editingItemValue.trim();
    if (!newName || newName === oldName) { setEditingItem(null); return; }
    if (checklistItems.includes(newName)) { toast.error('Item name already exists'); return; }
    try {
      await tasksAPI.renameChecklistItem(task.id, subtask.id, { old_name: oldName, new_name: newName });
      toast.success(`Renamed "${oldName}" to "${newName}"`);
      setEditingItem(null);
      onReload();
    } catch (err) { toast.error('Failed to rename item'); }
  };

  const handleBulkRowStatus = async (clientId, itemName, status) => {
    if (!status || !periods?.length) return;
    try {
      await tasksAPI.bulkUpdateChecklistItemStatus(task.id, subtask.id, clientId, itemName, {
        status,
        periods,
      });
      const stageLabel = CHECKLIST_STAGES[status]?.label || status;
      toast.success(`"${itemName}" set to ${stageLabel} for all ${periods.length} period(s)`);
      onReload();
    } catch (err) {
      toast.error('Failed to bulk-update row');
    }
  };

  const handleAddNote = async (clientId) => {
    const text = (noteInputs[clientId] || '').trim();    if (!text) return;
    setSubmittingNote(prev => ({ ...prev, [clientId]: true }));
    try {
      await tasksAPI.addClientNote(task.id, subtask.id, clientId, { text });
      setNoteInputs(prev => ({ ...prev, [clientId]: '' }));
      onReload();
    } catch (err) {
      toast.error('Failed to add note');
    } finally {
      setSubmittingNote(prev => ({ ...prev, [clientId]: false }));
    }
  };

  // Compute stats
  const totalCells = subtaskClients.length * checklistItems.length * periods.length;
  let doneCount = 0;
  subtaskClients.forEach(ce => {
    const cs = ce.checklist_status || {};
    checklistItems.forEach(item => {
      periods.forEach(p => {
        if ((cs[item] || {})[p] === 'done') doneCount++;
      });
    });
  });

  // Per-client completion
  const getClientCompletion = (ce) => {
    const cs = ce.checklist_status || {};
    let done = 0, total = checklistItems.length * periods.length;
    checklistItems.forEach(item => {
      periods.forEach(p => {
        if ((cs[item] || {})[p] === 'done') done++;
      });
    });
    return { done, total, pct: total > 0 ? Math.round(done / total * 100) : 0 };
  };

  const getColWidth = () => {
    if (periods.length <= 4) return 'w-[120px]';
    if (periods.length <= 6) return 'w-[90px]';
    return 'w-[75px]';
  };
  const colWidth = getColWidth();

  return (
    <div className="space-y-4">
      {/* Stats */}
      <div className="flex items-center gap-4 text-sm">
        <Badge variant="secondary">{subtaskClients.length} clients</Badge>
        <Badge variant="secondary">{checklistItems.length} checklist items</Badge>
        <Badge variant="secondary" className="bg-green-50 text-green-700">{doneCount}/{totalCells} done ({totalCells > 0 ? Math.round(doneCount / totalCells * 100) : 0}%)</Badge>
      </div>

      {/* Legend */}
      <div className="flex flex-wrap items-center gap-2 text-[11px]">
        <span className="text-muted-foreground font-medium mr-1">Status:</span>
        {Object.entries(CHECKLIST_STAGES).map(([key, val]) => (
          <span key={key} className={`inline-flex items-center px-2 py-0.5 rounded border text-[11px] font-medium ${val.color}`}>
            {val.short} = {val.label}
          </span>
        ))}
      </div>

      {/* Tabs: Checklist | Notes */}
      <div className="flex items-center gap-1 border-b">
        <button
          type="button"
          onClick={() => setActiveTab('checklist')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${activeTab === 'checklist' ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'}`}
          data-testid="checklist-tab-grid"
        >Checklist</button>
        <button
          type="button"
          onClick={() => setActiveTab('notes')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors flex items-center gap-1.5 ${activeTab === 'notes' ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'}`}
          data-testid="checklist-tab-notes"
        >
          <MessageSquare className="h-4 w-4" /> Notes
        </button>
      </div>

      {/* Admin: Manage Checklist Items */}
      {isAdmin && (
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs font-medium text-muted-foreground">Checklist Items:</span>
          {checklistItems.map(item => (
            editingItem === item ? (
              <div key={item} className="flex items-center gap-1">
                <Input
                  value={editingItemValue}
                  onChange={e => setEditingItemValue(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') handleRenameChecklistItem(item); if (e.key === 'Escape') setEditingItem(null); }}
                  className="h-7 text-xs w-40"
                  autoFocus
                  data-testid={`edit-checklist-item-input-${item}`}
                />
                <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-green-600" onClick={() => handleRenameChecklistItem(item)}>
                  <Check className="h-3.5 w-3.5" />
                </Button>
                <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => setEditingItem(null)}>
                  <X className="h-3.5 w-3.5" />
                </Button>
              </div>
            ) : (
              <Badge key={item} variant="outline" className="text-xs gap-1 pr-1">
                {item}
                <button onClick={() => { setEditingItem(item); setEditingItemValue(item); }} className="ml-0.5 text-muted-foreground hover:text-primary" title="Rename" data-testid={`rename-checklist-${item}`}>
                  <Pencil className="h-2.5 w-2.5" />
                </button>
                <button onClick={() => handleRemoveChecklistItem(item)} className="text-muted-foreground hover:text-red-500" title="Delete" data-testid={`delete-checklist-${item}`}>
                  <X className="h-3 w-3" />
                </button>
              </Badge>
            )
          ))}
          <div className="flex items-center gap-1">
            <Input
              placeholder="Add item..."
              value={newItem}
              onChange={e => setNewItem(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleAddChecklistItem()}
              className="h-7 text-xs w-36"
              data-testid="add-checklist-item-input"
            />
            <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={handleAddChecklistItem} data-testid="add-checklist-item-btn">
              <Plus className="h-3.5 w-3.5" />
            </Button>
          </div>
        </div>
      )}

      {/* Filter (only on Checklist tab) */}
      {activeTab === 'checklist' && (
      <div className="flex items-center gap-2">
        <span className="text-xs font-medium text-muted-foreground"><Filter className="h-3.5 w-3.5 inline mr-1" />Filter:</span>
        <div className="relative">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
          <Input
            placeholder="Client name..."
            value={filterClient}
            onChange={e => setFilterClient(e.target.value)}
            className="h-8 text-xs w-48 pl-7"
            data-testid="checklist-filter-client"
          />
        </div>
        {filterClient && (
          <Button variant="ghost" size="sm" className="h-8 text-xs gap-1" onClick={() => setFilterClient('')}>
            <X className="h-3.5 w-3.5" /> Clear
          </Button>
        )}
        <span className="text-xs text-muted-foreground ml-auto">{filteredClients.length} client(s)</span>
        {canManage && (
          <Button
            variant="outline"
            size="sm"
            className="h-8 text-xs gap-1"
            onClick={() => setClientPickerOpen(true)}
            data-testid="checklist-manage-clients"
          >
            <Users2 className="h-3.5 w-3.5" /> Manage clients
          </Button>
        )}
      </div>
      )}

      {/* Client Expandable Rows (Checklist tab) */}
      {activeTab === 'checklist' && (
      <Card>
        <CardContent className="p-0">
          {filteredClients.length === 0 ? (
            <div className="py-12 text-center text-sm text-muted-foreground">No clients</div>
          ) : (
            <div className="divide-y">
              {filteredClients.map(ce => {
                const isExpanded = expandedClients.has(ce.client_id);
                const completion = getClientCompletion(ce);
                const clientName = getClientName(ce.client_id);
                const cs = ce.checklist_status || {};

                return (
                  <div key={ce.client_id} data-testid={`checklist-client-${ce.client_id}`}>
                    {/* Client Header Row */}
                    <div
                      className="flex items-center gap-3 px-4 py-3 hover:bg-muted/30 transition-colors"
                    >
                      <button
                        type="button"
                        onClick={() => toggleClient(ce.client_id)}
                        className="flex items-center gap-3 cursor-pointer flex-1 min-w-0 text-left"
                        data-testid={`expand-client-${ce.client_id}`}
                      >
                        {isExpanded ? <ChevronDown className="h-4 w-4 text-muted-foreground flex-shrink-0" /> : <ChevronRight className="h-4 w-4 text-muted-foreground flex-shrink-0" />}
                        <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center text-sm font-medium text-primary flex-shrink-0">
                          {clientName[0]?.toUpperCase()}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">{clientName}</p>
                        </div>
                      </button>
                      {/* Folder Path (Audit boards) — per-client free-text field. Inline-edit, saves on blur/Enter. */}
                      <div className="flex items-center gap-1 flex-shrink-0" onClick={e => e.stopPropagation()}>
                        <Input
                          value={pathDrafts[ce.client_id] !== undefined ? pathDrafts[ce.client_id] : (ce.folder_path || '')}
                          onChange={e => setPathDrafts(prev => ({ ...prev, [ce.client_id]: e.target.value }))}
                          onBlur={() => saveFolderPath(ce.client_id)}
                          onKeyDown={e => {
                            if (e.key === 'Enter') { e.currentTarget.blur(); }
                            if (e.key === 'Escape') {
                              setPathDrafts(prev => { const n = { ...prev }; delete n[ce.client_id]; return n; });
                              e.currentTarget.blur();
                            }
                          }}
                          placeholder="Folder path / Drive link..."
                          disabled={!canManage || savingPath[ce.client_id]}
                          className="h-7 text-[11px] w-56"
                          data-testid={`folder-path-${ce.client_id}`}
                        />
                        {ce.folder_path && /^https?:\/\//i.test(ce.folder_path) && (
                          <a href={ce.folder_path} target="_blank" rel="noopener noreferrer"
                             className="text-[10px] text-primary hover:underline px-1"
                             title="Open folder"
                             data-testid={`open-folder-${ce.client_id}`}>↗</a>
                        )}
                        {/* Copy folder path to clipboard */}
                        <button
                          type="button"
                          onClick={() => copyFolderPath(ce.client_id)}
                          disabled={!(ce.folder_path)}
                          className="p-1 rounded hover:bg-muted text-muted-foreground hover:text-primary disabled:opacity-40 disabled:hover:bg-transparent"
                          title={ce.folder_path ? 'Copy folder path' : 'Save a folder path first'}
                          data-testid={`copy-folder-${ce.client_id}`}
                        >
                          <Copy className="h-3.5 w-3.5" />
                        </button>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <div className="w-24 h-2 bg-gray-100 rounded-full overflow-hidden">
                          <div className="h-full bg-green-500 rounded-full transition-all" style={{ width: `${completion.pct}%` }} />
                        </div>
                        <span className="text-xs text-muted-foreground w-16 text-right">{completion.done}/{completion.total}</span>
                        <Badge variant="secondary" className={`text-[10px] ${
                          completion.pct === 100 ? 'bg-green-100 text-green-700'
                            : completion.pct > 0 ? 'bg-blue-100 text-blue-700'
                            : 'bg-gray-100 text-gray-500'
                        }`}>
                          {completion.pct}%
                        </Badge>
                      </div>
                    </div>

                    {/* Expanded: Checklist Items Grid */}
                    {isExpanded && checklistItems.length > 0 && (
                      <div className="border-t bg-muted/10">
                        <div className="w-full overflow-x-auto">
                          <div style={{ width: '100%', minWidth: `${280 + periods.length * 100 + 64}px` }}>
                            {/* Period Header */}
                            <div className="flex items-center border-b bg-muted/20">
                              <div className="flex-1 min-w-[224px] px-6 py-2 text-[11px] font-semibold text-muted-foreground sticky left-0 bg-muted/20 z-10">Checklist Item</div>
                              {periods.map(p => (
                                <div key={p} className={`flex-shrink-0 text-center py-2 border-l ${colWidth}`}>
                                  <div className="text-[11px] font-semibold text-muted-foreground">{getPeriodLabel(p)}</div>
                                </div>
                              ))}
                              <div className="flex-shrink-0 w-[64px] text-center py-2 border-l">
                                <div className="text-[11px] font-semibold text-muted-foreground">Note</div>
                              </div>
                            </div>
                            {/* Item Rows */}
                            {checklistItems.map((item, idx) => (
                              <div key={item} className={`flex items-center border-b last:border-0 ${idx % 2 === 0 ? '' : 'bg-muted/5'}`}>
                                <div className="flex-1 min-w-[224px] px-6 py-2 sticky left-0 bg-background z-10 flex items-center gap-3">
                                  <span className="text-sm flex-1 truncate" title={item}>{item}</span>
                                  {(item === 'Drafting of Financial Statements' || item === 'GST ITC Reconciliation') && (
                                    <span
                                      className="inline-flex items-center gap-1 text-[10px] font-medium text-sky-700 bg-sky-50 border border-sky-200 rounded px-1.5 py-0.5"
                                      title={`Auto-synced with the ${item} board. Updates here propagate there.`}
                                      data-testid={`consolidated-tracker-sync-badge-${ce.client_id}-${item.replace(/\s+/g, '-')}`}
                                    >
                                      <RefreshCw className="h-2.5 w-2.5" /> Synced
                                    </span>
                                  )}
                                  {canManage && periods.length > 1 && (
                                    <Select value="" onValueChange={(v) => handleBulkRowStatus(ce.client_id, item, v)}>
                                      <SelectTrigger
                                        className="h-7 w-[120px] text-[10px] border-dashed text-muted-foreground px-2"
                                        title={`Set all ${periods.length} months for ${item}`}
                                        data-testid={`bulk-row-${ce.client_id}-${item}`}
                                      >
                                        <SelectValue placeholder={`Set all ${periods.length} →`} />
                                      </SelectTrigger>
                                      <SelectContent>
                                        {Object.entries(CHECKLIST_STAGES).map(([k, v]) => (
                                          <SelectItem key={k} value={k}>
                                            <span className="text-xs">{v.short} — {v.label}</span>
                                          </SelectItem>
                                        ))}
                                      </SelectContent>
                                    </Select>
                                  )}
                                </div>
                                {periods.map(p => {
                                  const status = (cs[item] || {})[p] || 'not_started';
                                  const stageInfo = CHECKLIST_STAGES[status] || CHECKLIST_STAGES.not_started;
                                  return (
                                    <div key={p} className={`flex-shrink-0 border-l px-1 py-1.5 ${colWidth}`}>
                                      {canManage ? (
                                        <Select value={status} onValueChange={v => handleStatusChange(ce.client_id, item, p, v)}>
                                          <SelectTrigger className={`h-7 w-full text-[11px] font-bold px-1 justify-center border-0 ${stageInfo.color}`} data-testid={`cl-status-${item}-${p}`}>
                                            <SelectValue>{stageInfo.short}</SelectValue>
                                          </SelectTrigger>
                                          <SelectContent>
                                            {Object.entries(CHECKLIST_STAGES).map(([k, v]) => (
                                              <SelectItem key={k} value={k}><span className="text-xs">{v.short} — {v.label}</span></SelectItem>
                                            ))}
                                          </SelectContent>
                                        </Select>
                                      ) : (
                                        <div className={`h-7 flex items-center justify-center text-[11px] font-bold rounded ${stageInfo.color}`}>
                                          {stageInfo.short}
                                        </div>
                                      )}
                                    </div>
                                  );
                                })}
                                {/* Per-checklist-item note (shared across all periods for that item × client). */}
                                <div className="flex-shrink-0 w-[64px] border-l flex items-center justify-center py-1.5">
                                  {(() => {
                                    const itemNote = ce.item_notes?.[item] || '';
                                    const has = itemNote.length > 0;
                                    return (
                                      <button
                                        type="button"
                                        onClick={() => openItemNotePopover(ce.client_id, item)}
                                        disabled={!canManage}
                                        className={`h-7 w-7 rounded inline-flex items-center justify-center transition-colors disabled:opacity-40
                                          ${has
                                            ? 'bg-amber-50 text-amber-700 hover:bg-amber-100 border border-amber-200'
                                            : 'text-muted-foreground hover:bg-muted hover:text-primary border border-dashed border-muted'}`}
                                        title={has ? itemNote : 'Add note for this item'}
                                        data-testid={`item-note-${ce.client_id}-${item.replace(/\s+/g, '-')}`}
                                      >
                                        <StickyNote className="h-3.5 w-3.5" />
                                      </button>
                                    );
                                  })()}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>

                        {/* Notes Section — admin/staff can leave remarks per client */}
                        {(() => {
                          const notes = ce.notes || [];
                          return (
                            <div className="border-t bg-background px-6 py-3" data-testid={`checklist-notes-${ce.client_id}`}>
                              <div className="flex items-center gap-2 mb-2">
                                <MessageSquare className="h-3.5 w-3.5 text-muted-foreground" />
                                <span className="text-xs font-semibold text-muted-foreground">Notes</span>
                                <Badge variant="secondary" className="text-[10px] h-4 px-1.5">
                                  {notes.length}
                                </Badge>
                              </div>
                              {notes.length === 0 ? (
                                <p className="text-xs text-muted-foreground italic mb-2">No notes yet — add a remark below</p>
                              ) : (
                                <div className="space-y-2 mb-2 max-h-44 overflow-y-auto pr-1">
                                  {notes.map(note => (
                                    <div key={note.id} className="flex gap-2.5 text-xs" data-testid={`note-${note.id}`}>
                                      <div className="h-6 w-6 rounded-full bg-muted flex items-center justify-center text-[10px] font-medium text-muted-foreground flex-shrink-0 mt-0.5">
                                        {(note.author_name || '?')[0].toUpperCase()}
                                      </div>
                                      <div className="flex-1 min-w-0">
                                        <div className="flex items-baseline gap-2">
                                          <span className="text-[11px] font-semibold">{note.author_name}</span>
                                          <span className="text-[10px] text-muted-foreground">
                                            {new Date(note.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}, {new Date(note.created_at).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
                                          </span>
                                        </div>
                                        <p className="text-xs text-foreground/90 mt-0.5 whitespace-pre-wrap break-words">{note.text}</p>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              )}
                              {canManage && (
                                <div className="flex gap-2">
                                  <Input
                                    type="text"
                                    value={noteInputs[ce.client_id] || ''}
                                    onChange={(e) => setNoteInputs(prev => ({ ...prev, [ce.client_id]: e.target.value }))}
                                    onKeyDown={(e) => {
                                      if (e.key === 'Enter') {
                                        e.preventDefault();
                                        handleAddNote(ce.client_id);
                                      }
                                    }}
                                    placeholder="Type a remark and press Enter..."
                                    className="h-8 text-xs"
                                    data-testid={`checklist-note-input-${ce.client_id}`}
                                  />
                                  <Button
                                    size="sm"
                                    className="h-8 px-3"
                                    onClick={() => handleAddNote(ce.client_id)}
                                    disabled={!(noteInputs[ce.client_id] || '').trim() || !!submittingNote[ce.client_id]}
                                    data-testid={`checklist-note-submit-${ce.client_id}`}
                                  >
                                    <Send className="h-3.5 w-3.5" />
                                  </Button>
                                </div>
                              )}
                            </div>
                          );
                        })()}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
      )}

      {/* Notes Tab — aggregate view of all clients' notes for this subtask */}
      {activeTab === 'notes' && (
        <div className="space-y-4" data-testid="checklist-notes-tab">
          {subtaskClients.length === 0 ? (
            <div className="py-12 text-center text-sm text-muted-foreground">No clients assigned to this subtask</div>
          ) : (
            subtaskClients.map(ce => {
              const notes = ce.notes || [];
              const clientName = getClientName(ce.client_id);
              return (
                <Card key={ce.client_id}>
                  <CardContent className="p-0">
                    <div className="px-5 py-3 border-b bg-muted/20 flex items-center gap-2">
                      <div className="h-7 w-7 rounded-full bg-primary/10 flex items-center justify-center text-xs font-medium text-primary flex-shrink-0">
                        {clientName?.[0]?.toUpperCase()}
                      </div>
                      <span className="font-medium text-sm">{clientName}</span>
                      <Badge variant="secondary" className="text-[10px] ml-auto">{notes.length} note{notes.length !== 1 ? 's' : ''}</Badge>
                    </div>
                    <div className="px-5 py-3 space-y-3">
                      {notes.length === 0 && (
                        <p className="text-sm text-muted-foreground italic">No notes yet</p>
                      )}
                      {notes.map(note => (
                        <div key={note.id} className="flex gap-3" data-testid={`checklist-aggregate-note-${note.id}`}>
                          <div className="h-7 w-7 rounded-full bg-muted flex items-center justify-center text-[10px] font-medium text-muted-foreground flex-shrink-0 mt-0.5">
                            {(note.author_name || '?')[0].toUpperCase()}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-baseline gap-2">
                              <span className="text-xs font-semibold">{note.author_name}</span>
                              <span className="text-[10px] text-muted-foreground">
                                {new Date(note.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}, {new Date(note.created_at).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
                              </span>
                            </div>
                            <p className="text-sm mt-0.5 whitespace-pre-wrap">{note.text}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                    {canManage && (
                      <div className="px-5 py-3 border-t flex gap-2">
                        <Input
                          type="text"
                          value={noteInputs[ce.client_id] || ''}
                          onChange={(e) => setNoteInputs(prev => ({ ...prev, [ce.client_id]: e.target.value }))}
                          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddNote(ce.client_id); } }}
                          placeholder="Type a note..."
                          className="flex-1 text-sm"
                          data-testid={`checklist-aggregate-note-input-${ce.client_id}`}
                        />
                        <Button
                          size="sm"
                          onClick={() => handleAddNote(ce.client_id)}
                          disabled={!(noteInputs[ce.client_id] || '').trim() || !!submittingNote[ce.client_id]}
                          data-testid={`checklist-aggregate-note-submit-${ce.client_id}`}
                        >
                          <Send className="h-4 w-4" />
                        </Button>
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })
          )}
        </div>
      )}
      <ClientPickerDialog
        open={clientPickerOpen}
        onOpenChange={setClientPickerOpen}
        clients={clients}
        linkedIds={subtaskClients.map(c => c.client_id)}
        onApply={handleApplyClientPicker}
        title={`Manage clients for ${subtask.title}`}
        subtitle={`${subtaskClients.length} clients currently linked. Tick to add, untick to remove.`}
      />

      {/* Per-checklist-item note dialog */}
      <Dialog open={!!openItemNote} onOpenChange={(o) => { if (!o) setOpenItemNote(null); }}>
        <DialogContent className="max-w-md" data-testid="item-note-dialog">
          <DialogHeader>
            <DialogTitle className="text-sm">
              Note — {openItemNote?.item}
              <span className="block text-xs font-normal text-muted-foreground mt-1">
                {openItemNote ? (clients.find(c => c.id === openItemNote.clientId)?.name || 'Client') : ''}
              </span>
            </DialogTitle>
          </DialogHeader>
          <Textarea
            value={itemNoteDraft}
            onChange={(e) => setItemNoteDraft(e.target.value)}
            placeholder="e.g. Books closed on 31 Mar — sent draft to partner..."
            className="text-sm min-h-[120px]"
            data-testid="item-note-textarea"
          />
          <p className="text-[10px] text-muted-foreground">
            One note per item per client (shared across all periods). Leave blank to clear.
          </p>
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setOpenItemNote(null)} disabled={savingItemNote}>Cancel</Button>
            <Button size="sm" onClick={saveItemNote} disabled={savingItemNote} className="gap-1" data-testid="item-note-save">
              <Save className="h-3.5 w-3.5" /> {savingItemNote ? 'Saving...' : 'Save note'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ChecklistGrid;
